import {DataContentWrapper} from '@hconnect/common/containers'
import {useNotification} from '@hconnect/uikit'
import {useUpmPlantContext} from '@hconnect/upm'
import {yupResolver} from '@hookform/resolvers/yup'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {EditKpisModalView} from '../components/edit'
import {FEATURE_NAMESPACE} from '../consts'
import {useSaveKpiDefinitions, useSlaKpiTabValues, type TabFilterValue} from '../hooks'
import {mapEditFormValuesToPayload, mapKpiTabValuesToEditFormValues} from '../mappers'
import {KpiEditFormValues} from '../types'
import {getEditKpiDefinitionsValidationSchema} from '../validators'

type EditKpisModalContainerProps = {
  open: boolean
  onClose: () => void
  year: number
  tabFilter: TabFilterValue
  onTabFilterChange: (value: TabFilterValue) => void
  searchTerm: string
  onSearchChange: (value: string) => void
}

export const EditKpisModalContainer = ({
  open,
  onClose,
  year,
  tabFilter,
  onTabFilterChange,
  searchTerm,
  onSearchChange
}: EditKpisModalContainerProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {notify} = useNotification()
  const {upmId: plantUpmId, plantId} = useUpmPlantContext()

  // Fetch KPI tab values
  const {data, isLoading, isError, refetch} = useSlaKpiTabValues({
    plantUpmId,
    year
  })

  // Memoize validation schema - only recreate when t changes
  const validationSchema = useMemo(() => getEditKpiDefinitionsValidationSchema(t), [t])

  // Cache the resolver to prevent recreation
  const resolverRef = useRef(yupResolver(validationSchema))
  useEffect(() => {
    resolverRef.current = yupResolver(validationSchema)
  }, [validationSchema])

  const defaultValues = useMemo((): KpiEditFormValues => {
    if (!data) {
      return {tabGroups: []}
    }
    return mapKpiTabValuesToEditFormValues(data)
  }, [data])

  const methods = useForm<KpiEditFormValues>({
    mode: 'onSubmit', // Only validate on submit - critical for performance
    reValidateMode: 'onSubmit',
    resolver: resolverRef.current,
    defaultValues
  })

  const {
    handleSubmit,
    reset,
    formState: {isSubmitting, isDirty}
  } = methods

  // Reset form when data changes
  useEffect(() => {
    if (data) {
      const formValues: KpiEditFormValues = mapKpiTabValuesToEditFormValues(data)
      reset(formValues)
    }
  }, [data, reset])

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset(defaultValues)
    }
  }, [open, reset, defaultValues])

  const {mutateAsync: saveKpiDefinitions} = useSaveKpiDefinitions({plantUpmId})

  const onSave = useCallback(async () => {
    await handleSubmit(
      async (formValues) => {
        const payload = mapEditFormValuesToPayload(formValues)
        await saveKpiDefinitions(payload)
        onClose()
      },
      (errors) => {
        console.error('Form validation errors:', errors)
        notify('error', t('editKpisModal.message.validationFailed'))
      }
    )()
  }, [handleSubmit, saveKpiDefinitions, onClose, notify, t])

  // Keep save enabled - validation happens on submit
  const isSaveDisabled = isSubmitting

  return (
    <DataContentWrapper
      showLoader={isLoading && open}
      isError={isError}
      data={data}
      retryFunction={() => void refetch()}
      progressSx={{color: 'primary.main'}}
    >
      {data && (
        <FormProvider {...methods}>
          <EditKpisModalView
            open={open}
            onClose={onClose}
            onSave={onSave}
            isSubmitting={isSubmitting}
            isSaveDisabled={isSaveDisabled}
            isDirty={isDirty}
            tabFilter={tabFilter}
            onTabFilterChange={onTabFilterChange}
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            initialTabGroups={defaultValues.tabGroups}
            plantId={plantId}
          />
        </FormProvider>
      )}
    </DataContentWrapper>
  )
}
